import { GetResourceResponse } from "@microtica/ms-engine-sdk";
import { CloudUploadOutlined, GlobalOutlined } from "@ant-design/icons";
import { Alert, PageHeader, Skeleton, Space, Tabs } from "antd";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getElasticSearchService, getEnvironmentService } from "../../backend";
import PageContentWrapper from "../../components/PageContentWrapper";
import { currentProjectState } from "../../recoil/project";
import { ComponentConfig, ComponentDetails, IResourceDetails, ResourceConfigurationAndStatus, StatusColor } from "../../types";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import PageHeaderBreadcrumb from "../../components/PageHeaderBreadcrumb";
import PageHeaderContent from "../../components/PageHeaderContent";
import { getResourceStatusColor, mapDeploymentStatus } from "../../components/DeploymentStatusTag";
import GitCommitLink from "../../components/GitCommitLink";
import { handleDeleteResource } from "../../utils/environment/handle-delete-resource";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import MenuItemLabel from "../../components/MenuItemLabel";
import { undeployResource } from "../../utils/environment/handle-undeploy-resource";
import { isResourceActionEnabled } from "../../utils/environment";
import { useCurrentProject } from "../../contexts/Project";
import { getResourcesReferencingParticularResource } from "../../utils/environment/get-resource-references";

export default function ResourceRoot() {
    const { projectId, envId, resourceName } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [resourceDetails, setResourceDetails] = useState<IResourceDetails>();
    const [activeTab, setActiveTab] = useState("overview");
    const [envName, setEnvName] = useState("");
    const [resourceDeploymentActions, setResourceDeploymentActions] = useState<{ undeployEnabled: boolean, deleteEnabled: boolean }>();
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const { updateCurrentProject } = useCurrentProject();
    const currentProject = useRecoilValue(currentProjectState);
    const [deployedResourcesConfigurations, setDeployedResourcesConfigurations] = useState<ResourceConfigurationAndStatus>();
    const [resourcesConfigurations, setResourcesConfigurations] = useState<{ [resourceName: string]: ComponentConfig[] }>();
    const [notFoundError, setNotFoundError] = useState<boolean>(false);

    useEffect(() => {
        // change the current project if the projectId in the URL is different from the current project saved in local storage
        // this is the root component for all Resource related routes, so no need to do this in other places
        if (!!projectId && projectId !== currentProject.id) {
            updateCurrentProject(projectId);
        }
    }, [projectId])

    useEffect(() => {
        loadData();
        const interval = setInterval(async () => {
            await loadData();
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    async function loadData() {
        const [
            { data: resourceDetails },
            { data: environment },
            { data: { outputs } },
            { data: { response: envDeployments } }
        ] = await Promise.all([
            getEnvironmentService().getResource(envId!, resourceName!, projectId!),
            getEnvironmentService().getStageDetails(envId!, projectId!),
            getEnvironmentService().getOutputsForResource(envId!, resourceName!, projectId!)
                .catch(_ => ({ data: { outputs: undefined } })),
            getElasticSearchService().getStageDeploymentHistory(projectId!, envId!)
        ]).catch(err => {
            // display PageNotFound if one of the API calls failed with 404 error
            if (err?.response?.data?.code === 404) {
                setNotFoundError(true);
            }
            throw err;
        });
        setNotFoundError(false);
        const { component } = resourceDetails as GetResourceResponse & { component: ComponentDetails };
        let accessUrl = outputs?.find(o => o.key === "AccessUrl")?.value;
        let CNAME = outputs?.find(o => o.key === "CNAME")?.value;
        setEnvName(environment.name);
        setResourceDetails(prevResourceDetails => {
            // if outputs is undefined, an error occurred while fetching them
            if (!outputs) {
                // keep accessUrl and CNAME values, if the second to last outputs call was successful
                if (!!prevResourceDetails?.domain.accessUrl) {
                    accessUrl = prevResourceDetails.domain.accessUrl;
                }
                if (!!prevResourceDetails?.domain.CNAME) {
                    CNAME = prevResourceDetails.domain.CNAME
                }
            }

            return {
                name: resourceDetails.name,
                awsAccountId: environment.awsAccountId,
                awsRegion: environment.awsRegion,
                status: resourceDetails.status!,
                version: resourceDetails.componentVersion,
                component,
                ...environment.resources.find(resource => resource.name === resourceDetails.name),
                isPublic: !!component.isPublic,
                currentDeploymentId: environment.currentDeploymentId,
                previousDeploymentId: envDeployments.find(d => d.deploymentId === environment.currentDeploymentId)?.lastDeploymentId,
                pipelineId: (resourceDetails as GetResourceResponse & { component: ComponentDetails }).component.pipelineId,
                isDeployed: !["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(resourceDetails?.status || ""),
                deployments: envDeployments.filter(d => d.resources?.find(res => res.name === resourceDetails.name)).map(d => {
                    const resourceDeployment = d.resources.find(res => res.name === resourceDetails.name)!;
                    const { metadata } = resourceDeployment?.component!;
                    return {
                        id: d.deploymentId,
                        name: d.name,
                        version: metadata.commit.version,
                        branch: metadata.commit ? metadata.commit.name : "n/a",
                        commitType: metadata?.commit.type,
                        commitMessage: metadata.commit ? metadata.commit.message : "n/a",
                        repositoryUrl: metadata.repository,
                        pipeline: {
                            id: resourceDeployment.component.pipelineId,
                            buildId: resourceDeployment.component.version,
                            public: !!resourceDeployment.component.isPublic
                        },
                        createdAt: moment(resourceDeployment?.component.metadata.commit.date || resourceDeployment.lastDeployed).fromNow(),
                        createdBy: d.initiator.email,
                        initiator: metadata.commit.user,
                        avatarUrl: metadata.commit.user.avatar,
                        status: {
                            name: d.status,
                            errors: d.logs?.events?.[resourceDetails.name]?.errorMessages
                        },
                        timestamp: moment(d.timestamp).fromNow()
                    }
                }),
                domain: {
                    // if accessUrl equals the loadbalancer's DNS name (CNAME), display "Assign Custom Domain" btn 
                    // We must lowercase the CNAME since the URL utility is automatically lowecasing the hostname
                    displayAssignDomainBtn: !!accessUrl && !!CNAME && new URL(accessUrl).hostname === CNAME.toLowerCase(),
                    eligibleForDomainConfiguration: !!accessUrl && !!CNAME,
                    accessUrl,
                    CNAME
                }
            } as any
        });

        // Set resource's action buttons status
        setResourceDeploymentActions({
            undeployEnabled: isResourceActionEnabled({
                action: "undeploy",
                resourceStatus: resourceDetails.status!,
                envStatus: environment.status
            }),
            deleteEnabled: isResourceActionEnabled({
                action: "delete",
                resourceStatus: resourceDetails.status!,
                envStatus: environment.status
            }),
        });

        // Set configuration for each resource. Mapped like this, in order to be consistent with the undeployResource
        // logic in EnvironmentInfrastructure.tsx
        const deployedResourcesConfigurations = environment.resources
            .filter(r => !["NOT_DEPLOYED", "DELETE_COMPLETE", "ROLLBACK_COMPLETE"].includes(r.status))
            .reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr.name]: {
                        status: curr.status,
                        configurations: curr.configurations
                    }
                }
            }, {} as ResourceConfigurationAndStatus);

        setDeployedResourcesConfigurations(deployedResourcesConfigurations);
    }

    useEffect(() => {
        setActiveTab(location.pathname.split("/")[7] || "overview");
    }, [location.pathname]);

    const headerBreadcrumb = resourceDetails ?
        <PageHeaderBreadcrumb
            items={[
                { link: "/projects", name: "Projects" },
                { link: `/projects/${projectId}`, name: currentProject.name },
                { link: `/projects/${projectId}/environments`, name: "Environments" },
                { link: `/projects/${projectId}/environments/${envId}`, name: envName },
                { link: `/projects/${projectId}/environments/${envId}/infrastructure`, name: "Resources" },
                { link: `/projects/${projectId}/environments/${envId}/resources/${resourceDetails?.name}`, name: resourceDetails?.name }
            ]}
        /> : <Skeleton.Button active={true} size="small" />;

    const headerTabs = (
        <Tabs
            activeKey={activeTab}
            onChange={(activeKey) => {
                navigate(activeKey);
                setActiveTab(activeKey)
            }}
        >
            <Tabs.TabPane tab="Overview" key="overview" />
            <Tabs.TabPane tab="Pipeline" key="pipeline" />
            <Tabs.TabPane tab="Deployments" key="deployments" />
            <Tabs.TabPane tab="Resource Settings" key="settings" />
        </Tabs>
    );

    const actionItems = [
        {
            label: "Configure Resource",
            key: "configure",
            onClick: () => navigate(`/projects/${projectId}/environments/${envId}/resources/${resourceName}/settings/general`)
        },
        {
            label: <MenuItemLabel
                text="Git Settings"
                showTooltip={resourceDetails?.isPublic!}
                tooltipText="Git settings are not available for resources managed by Microtica"
            />,
            key: "configure-git",
            onClick: () => navigate(`/projects/${projectId}/environments/${envId}/resources/${resourceName}/settings/git`),
            disabled: resourceDetails?.isPublic
        },
        { type: "divider" },
        {
            label: <MenuItemLabel
                text="Undeploy Resource"
                showTooltip={!resourceDeploymentActions?.undeployEnabled}
                tooltipText="Resource must be deployed in order to be able to perform this action"
            />,
            key: "undeploy",
            danger: resourceDeploymentActions?.undeployEnabled,
            disabled: !resourceDeploymentActions?.undeployEnabled,
            onClick: () => { handleUndeployResource(); }
        },
        {
            label: <MenuItemLabel
                text="Delete Resource"
                showTooltip={!resourceDeploymentActions?.deleteEnabled}
                tooltipText="Resource must be undeployed first in order to be able to perform this action"
            />,
            key: "delete",
            danger: resourceDeploymentActions?.deleteEnabled,
            disabled: !resourceDeploymentActions?.deleteEnabled,
            onClick: () => openConfirmModal({
                title: "Delete resource?",
                description: <Space direction="vertical">
                    <div>
                        The resource will be permanently deleted from this environment.
                    </div>
                    <div>
                        If you just want to undeploy it, choose <span style={{ fontStyle: 'italic' }}>Undeploy resource</span> from the same menu.
                    </div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
                confirmation: resourceName!,
                okText: "Delete",
                cancelText: "Cancel",
                onOk: () => {
                    return handleDeleteResource({
                        envId: envId!,
                        projectId: projectId!,
                        navigate,
                        resourceName: resourceName!,
                        componentId: resourceDetails?.component.id || "",
                        componentName: resourceDetails?.component.name || "",
                    });
                }
            })
        }
    ] as ItemType[];

    const status = resourceDetails && mapDeploymentStatus(resourceDetails?.status).status;
    const statusColor = getResourceStatusColor(resourceDetails?.status);
    const headerContent = resourceDetails ?
        <PageHeaderContent
            title={{
                prefix: "Resource",
                mainText: resourceDetails?.name
            }}
            summaryTag={{
                text: status!,
                color: statusColor
            }}
            summary={
                [StatusColor.Active, StatusColor.Processing, StatusColor.Failed].includes(statusColor) && resourceDetails?.deployments.length ?
                    [
                        ...resourceDetails?.currentDeploymentId ? [
                            {
                                primaryText:
                                    <Space size={4}>
                                        <CloudUploadOutlined />
                                        <Link to={`/projects/${projectId}/environments/${envId}/deployments/infrastructure/${resourceDetails.currentDeploymentId}`}>{resourceDetails.currentDeploymentId}</Link>
                                    </Space>
                            }] : [],
                        { secondaryText: resourceDetails.deployments[0].timestamp }, // last deployment timestamp
                        { secondaryText: <GitCommitLink version={resourceDetails.deployments[0].version} repoUrl={resourceDetails.deployments[0].repositoryUrl} branch={resourceDetails.deployments[0].branch} color="gray" /> }
                    ] : [
                        { secondaryText: `Version ${resourceDetails.version.substring(0, 7)}` }
                    ]
            }
            mainLinkButton={resourceDetails.domain.accessUrl ? {
                link: resourceDetails.domain.accessUrl,
                title: <><GlobalOutlined /> Visit</>
            } : undefined}
            actionItems={actionItems}
        /> : <Skeleton.Input />;

    const handleUndeployResource = async () => {
        // check if there are other resources that reference the one that is selected for undeployment
        const referencedByResources = getResourcesReferencingParticularResource(resourceName!, deployedResourcesConfigurations!);

        openConfirmModal({
            title: "Undeploy resource?",
            description: <Space direction="vertical">
                <div>
                    The resource will be undeployed from this environment. This means that it will not consume any resources on AWS but will be available to be redeployed.
                </div>
                <div>
                    If you want to completely remove it from this environment, choose <span style={{ fontStyle: 'italic' }}>Delete resource</span> from the same menu.
                </div>
                {
                    !!referencedByResources?.length &&
                    <Alert
                        message={
                            <div>
                                The <b>{resourceName!}</b> resource is being referenced by some active resources. If you proceed to <b>Undeploy</b> this resource, the following resources will also be undeployed:
                                <b> {referencedByResources.join(", ")}.</b>
                            </div>
                        }
                        type="error"
                    />
                }
            </Space>,
            confirmation: resourceName!,
            okText: "Undeploy",
            cancelText: "Cancel",
            onOk: () => {
                return undeployResource({
                    envId: envId!,
                    projectId: projectId!,
                    navigate,
                    resourceName: resourceName!
                });
            }
        })
    }

    return (
        <PageContentWrapper
            size="large"
            header={
                <PageHeader
                    title={headerBreadcrumb}
                    footer={headerTabs}
                    style={{ paddingTop: "8px" }}
                >
                    {headerContent}
                </PageHeader>
            }
            loading={!resourceDetails}
            notFoundError={notFoundError}
        >
            {resourceDetails ? <Outlet context={{ resourceDetails }} /> : <Skeleton />}
        </PageContentWrapper>
    );
}